import React, { FunctionComponent } from 'react';
import { Typography, Link as MuiLink, Box } from '@mui/material';
import * as Yup from 'yup';
import { FormikErrors, useFormik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormValidations, Constants } from '../../../utils';
import { AppThunkDispatch } from '../../../interfaces/redux.interface';
import { authActions, selectIsLoading } from '../../../redux/authSlice';
import { CircularButton, InputText, Spinner } from '../../../components';
import { paths } from '../..';
import useStyles from './SignInForm.styles';

const awsSignInError = 'The email or password you entered is incorrect';

interface FormValues {
  email: string;
  password: string;
  aws: boolean;
}

const testid = 'signin';

const SignInForm: FunctionComponent = () => {
  const classes = useStyles();
  const dispatch: AppThunkDispatch = useDispatch();
  const isLoading = useSelector(selectIsLoading);
  const navigate = useNavigate();

  const initialValues: FormValues = {
    email: '',
    password: '',
    aws: false,
  };

  const onSubmit = (
    values: FormValues,
    { setErrors }: { setErrors: (errors: FormikErrors<FormValues>) => void }
  ) => {
    setErrors({ aws: '' });

    dispatch(
      authActions.signIn({
        username: values.email.trim().toLowerCase(),
        password: values.password,
      },navigate)
    ).catch(() => setErrors({ aws: awsSignInError }));
  };

  const validationSchema = Yup.object().shape({
    email: FormValidations.email,
    password: FormValidations.passwordRequired,
  });

  const form = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
  });

  return (
    <form onSubmit={form.handleSubmit}>
      <Spinner show={isLoading} />
      <InputText
        id="email"
        name="email"
        onChange={form.handleChange}
        value={form.values.email}
        labelText="Email Address"
        placeholder="Eg: julia@healthysteps.org"
        errorMessage={form.errors.email}
        customClass={classes.input}
      />
      <InputText
        id="password"
        name="password"
        type={Constants.INPUT_TYPES.PASSWORD}
        onChange={form.handleChange}
        value={form.values.password}
        labelText="Password"
        placeholder="Enter your password"
        errorMessage={form.errors.password}
        customClass={classes.input}
      />
      {form.errors.aws && (
        <Typography
          className={classes.error}
          data-testid={`${testid}__error`}
        >
          {form.errors.aws}
        </Typography>
      )}

      <Box className={classes.forgotLinkWrapper}>
        <MuiLink
          component={Link}
          to={paths.forgotPassword}
          underline="hover"
          className={classes.forgotLink}
          data-testid={`${testid}__forgot-password`}
        >
          Forgot your password?
        </MuiLink>
      </Box>

      <CircularButton
        variant="contained"
        type="submit"
        color="primary"
        customClass={classes.submitButton}
        data-testid={`${testid}__submit`}
      >
        Sign in
      </CircularButton>

      <Link to="?request-access=true">
        <CircularButton
          variant="contained"
          type="button"
          color="secondary"
          customClass={classes.requestAccessButton}
          data-testid={`${testid}__request-account`}
        >
          Request an account
        </CircularButton>
      </Link>
    </form>
  );
};

export default SignInForm;
